import { useEffect, useRef, useState } from "react";

export interface QuickAction {
  label: string;
  onSelect: () => void;
  /** A destructive action (cancel): shown in the danger colour. */
  danger?: boolean;
}

const MENU_WIDTH = 190;
const ITEM_HEIGHT = 36;

/**
 * A "..." button that opens a small menu of actions for one table row. The menu is positioned against the viewport (not inside the
 * table), so the scrolling table never clips it, and it opens upward when there is no room below. Closes on a click elsewhere, Escape,
 * scroll or resize.
 */
export function QuickActions({ actions, label = "Actions" }: { actions: QuickAction[]; label?: string }) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const open = pos !== null;

  useEffect(() => {
    if (!open) return;
    const close = () => setPos(null);
    const onDown = (e: MouseEvent) => {
      if (menu.current?.contains(e.target as Node) || button.current?.contains(e.target as Node)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
        button.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    menu.current?.querySelector<HTMLButtonElement>("button")?.focus();
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  if (actions.length === 0) return null;

  function toggle() {
    if (open) return setPos(null);
    const rect = button.current?.getBoundingClientRect();
    if (!rect) return;
    const height = actions.length * ITEM_HEIGHT + 8;
    const below = rect.bottom + 4 + height <= window.innerHeight;
    setPos({
      top: below ? rect.bottom + 4 : Math.max(8, rect.top - 4 - height),
      left: Math.max(8, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8)),
    });
  }

  return (
    <>
      <button
        ref={button}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
        style={{
          background: open ? "var(--rc-surface-sunken)" : "none",
          border: "1px solid var(--rc-border)",
          borderRadius: "var(--rc-radius-md)",
          color: "var(--rc-text-muted)",
          cursor: "pointer",
          fontSize: 16,
          fontWeight: 700,
          lineHeight: 1,
          minHeight: 0,
          padding: "4px 10px",
        }}
      >
        &hellip;
      </button>
      {open && (
        <div
          ref={menu}
          role="menu"
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            width: MENU_WIDTH,
            zIndex: 60,
            padding: 4,
            background: "var(--rc-surface)",
            border: "1px solid var(--rc-border)",
            borderRadius: "var(--rc-radius-md)",
            boxShadow: "var(--rc-shadow-md, 0 8px 24px rgba(0,0,0,0.16))",
          }}
        >
          {actions.map((a) => (
            <button
              key={a.label}
              type="button"
              role="menuitem"
              onClick={() => {
                setPos(null);
                a.onSelect();
              }}
              style={{
                display: "block",
                width: "100%",
                height: ITEM_HEIGHT,
                minHeight: 0,
                padding: "0 12px",
                textAlign: "left",
                background: "none",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13.5,
                fontWeight: 600,
                color: a.danger ? "var(--rc-danger)" : "var(--rc-text)",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--rc-surface-sunken)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}
